import React, { ChangeEvent, useEffect, useState, useCallback } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { clientTypes } from "./clientTypes";

// Material-UI components
import {
  Box,
  Grid,
  TextField,
  FormControl,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";

// Internal dependencies
import {
  _addClient,
  _updateClientInformation,
} from "middlewares/ClientApi/client";
import { Client } from "../types";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import masterRegionsApi from "middlewares/RegionApi/region";
import masterPropertyApi from "middlewares/MasterProperty";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Spinner from "components/Spinner/Spinner";
import ClientSetTarget from "./ClientSetTarget";

// Interface for the props of the AddClient component
interface AddClientProps {
  handleCloseSnackBar: (state: AlertsProps) => void;
  hideDialogHandler: () => void;
  rowData?: Client | null;
  initialTab?: number;
  onClientUpdated?: (client: Client) => void;
  onClientCreated?: (client: Client) => void;
}

/**
 * AddClient component
 *
 * This component renders a form for adding or updating a user. It handles form submission, input changes, and API calls for saving user data.
 *
 * @param {AddClientProps} props - The props for the component.
 * @param {Function} props.handleCloseSnackBar - Function to handle closing the snackbar.
 * @param {Function} props.hideDialogHandler - Function to hide the dialog.
 * @returns {JSX.Element} A form for adding or updating a client.
 */
const AddClient = (props: AddClientProps) => {
  // State for user data and loading status
  const [client, setClient] = useState<Client & { imageFile?: File }>({
    clientId: 0,
    clientName: "",
    clientType: "",
    contact: 0,
    description: "",
    industry: "",
    region: "",
    targetYear: 0,
    createdBy: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    imageName: "",
    organizations: [],
  });

  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({ mode: "onSubmit", shouldUnregister: false });
  const [regionList, setRegionList] = useState<
    Array<{ regionId: number; regionName: string; value: string }>
  >([]);

  const [industryList, setIndustryList] = useState<
    Array<{ id: string; value: string; label?: string }>
  >([]);

  const [activeTab, setActiveTab] = useState(props.initialTab ?? 0);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  const isEditMode = client.clientId > 0;
  const clientImageTabIndex = 3;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const CURRENT_YEAR = new Date().getFullYear();

  useEffect(() => {
    if (props.rowData) {
      setClient({
        clientId: props.rowData.clientId || 0,
        clientName: props.rowData?.clientName ?? "",
        clientType: props.rowData?.clientType ?? "",
        description: props.rowData?.description ?? "",
        industry: props.rowData?.industry ?? "",
        region: props.rowData?.region ?? "",
        contact: props.rowData?.contact ?? 0,
        targetYear: props.rowData?.targetYear ?? 0,
        firstName: props.rowData?.firstName ?? "",
        lastName: props.rowData?.lastName ?? "",
        email: props.rowData?.email ?? "",
        phoneNumber: props.rowData?.phoneNumber ?? "",
        imageName: props.rowData?.imageName ?? "",
        organizations: props.rowData?.organizations ?? [],
        createdBy: props.rowData?.createdBy ?? "",
        createdAt:
          typeof props.rowData?.createdAt === "string"
            ? new Date(props.rowData.createdAt)
            : props.rowData?.createdAt,
      });
      setValue("ClientId", props.rowData?.clientId);
      setValue("ClientName", props.rowData?.clientName);
      setValue("ClientType", props.rowData?.clientType);
      setValue("Description", props.rowData?.description);
      setValue("Industry", props.rowData?.industry);
      setValue("Region", props.rowData?.region);
      setValue("FirstName", props.rowData?.firstName);
      setValue("LastName", props.rowData?.lastName);
      setValue("Email", props.rowData?.email);
      setValue("PhoneNumber", props.rowData?.phoneNumber);
      setValue("Contact", props.rowData?.contact);
      setValue("TargetYear", props.rowData?.targetYear);

      if (props.rowData.imageName) {
        setBase64Image(props.rowData.imageName);
        try {
          const file = convertBase64ToFile(
            props.rowData.imageName,
            "client-image",
          );
          setSelectedFile(file);
          setClient((prev) => ({ ...prev, imageFile: file }));
        } catch (err) {
          console.error("Failed to convert base64 image to File:", err);
        }
      }
    }
    getRegionsList();
    getIndustryList();
  }, [props.rowData, setValue]);

  /**
   * Handles the file drop event from the dropzone and starts base64 conversion.
   *
   * @param {File[]} acceptedFiles - Array of dropped files.
   */
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      convertToBase64(acceptedFiles[0]);
      setSelectedFile(acceptedFiles[0]);
      setClient((prev) => ({ ...prev, imageFile: acceptedFiles[0] }));
    }
  }, []);

  const convertToBase64 = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setBase64Image(reader.result as string);
      setClient((prev) => ({
        ...prev,
        imageName: (reader.result as string)
          ? (reader.result as string)
          : prev.imageName,
      }));
    };
    reader.onerror = (error) => console.error("Error converting file:", error);
  };

  function convertBase64ToFile(base64Data: string, fileName: string): File {
    const match = /^data:(.+);base64,(.*)$/.exec(base64Data);
    let mime = "image/png";
    let b64 = base64Data;
    if (match) {
      mime = match[1];
      b64 = match[2];
    }
    const byteChars = atob(b64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mime });
    return new File([blob], fileName, { type: mime });
  }

  // Fetch regions from API and set to state
  function getRegionsList() {
    masterRegionsApi
      ._getRegions()
      .then((res) => {
        if (!res || res.length === 0) {
          return;
        }
        const list = res.map((item) => ({
          regionId: item.regionId,
          regionName: item.regionName ?? "", // convert to string, fallback empty string
          value: item.regionId.toString(),
        }));

        setRegionList(list);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching regions:", err);
      });
  }

  // Fetch industry list from API and set to state
  function getIndustryList() {
    masterPropertyApi
      .getMasterPropertyListByCategoryAndType("Location", "Industry")
      .then((res) => {
        if (!res || res.length === 0) {
          return;
        }
        const list = res.map((item) => ({
          id: String(item.id ?? ""), // convert to string, fallback empty string
          value: String(item.id ?? ""),
          label: item.value,
        }));

        setIndustryList(list);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching industry list:", err);
      });
  }

  // Handle input change for text fields
  /**
   * Handles input changes for text fields.
   *
   * @param {ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e - The change event.
   */
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    const fieldMap: Record<string, keyof Client> = {
      ClientName: "clientName",
      ClientType: "clientType",
      Description: "description",
      Region: "region",
      Industry: "industry",
      Contact: "contact",
      TargetYear: "targetYear",
      FirstName: "firstName",
      LastName: "lastName",
      Email: "email",
      PhoneNumber: "phoneNumber",
    };

    if (e.target.name === "Email") {
      setValue(name, value, { shouldValidate: true });
    } else if (e.target.name === "PhoneNumber") {
      let value = e.target.value;

      // ✅ Allow only digits and "+"
      value = value.replace(/[^0-9+]/g, "");

      // ✅ Allow "+" only at the beginning
      if (value.indexOf("+") > 0) {
        value = value.replace(/\+/g, "");
      }

      // ✅ Enforce max length
      value = value.slice(0, 15);

      setClient((prev) => ({
        ...prev,
        phoneNumber: value,
      }));

      setValue("PhoneNumber", value, { shouldValidate: true });
      return;
    } else if (e.target.name === "TargetYear") {
      const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 4);
      const yearNumber = digitsOnly ? Number(digitsOnly) : 0;

      setClient((prev) => ({
        ...prev,
        targetYear: yearNumber,
      }));

      setValue("TargetYear", digitsOnly, { shouldValidate: true });

      return;
    } else {
      setValue(name, value, { shouldValidate: false });
    }

    const mappedKey = fieldMap[name] || name;
    setClient((prev) => ({
      ...prev,
      [mappedKey]: value,
    }));
  };

  const handleSelectInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent,
    child?: React.ReactNode,
  ) => {
    let name: string;
    let value: string;
    let textLabel: string = "";

    const fieldMap: Record<string, keyof Client> = {
      ClientName: "clientName",
      ClientType: "clientType",
      Description: "description",
      Region: "region",
      Industry: "industry",
      Contact: "contact",
      TargetYear: "targetYear",
      FirstName: "firstName",
      LastName: "lastName",
      Email: "email",
      PhoneNumber: "phoneNumber",
    };

    // If the event is fired by MUI Select
    if ("target" in e && typeof e.target.value === "string") {
      name = e.target.name!;
      value = e.target.value.toString();

      // Get the text/label from the selected option
      if (name === "ClientType") {
        const selectedType = clientTypes.find((t) => t.value === value);
        textLabel = selectedType?.label ?? value;
      } else if (name === "Region") {
        const selectedRegion = regionList.find((r) => r.value === value);
        textLabel = selectedRegion?.regionName ?? value;
      } else if (name === "Industry") {
        const selectedIndustry = industryList.find((i) => i.value === value);
        textLabel = selectedIndustry?.label ?? value;
      }
    } else {
      // TextField or TextArea
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      name = target.name;
      value = target.value;
      textLabel = value;
    }

    const mappedKey = fieldMap[name] || name;
    setClient((prev) => ({
      ...prev,
      [mappedKey]: textLabel || value,
    }));

    // Update react-hook-form value for validation
    setValue(name, textLabel || value, { shouldValidate: false });
  };

  useEffect(() => {
    if (panelHasErrors("panel1", errors)) {
      setActiveTab(0);
    } else if (panelHasErrors("panel2", errors)) {
      setActiveTab(1);
    }
  }, [errors]);

  const PANEL_FIELD_MAP: Record<string, string[]> = {
    panel1: [
      "ClientName",
      "ClientType",
      "Description",
      "Region",
      "Industry",
      "TargetYear",
    ],
    panel2: ["FirstName", "LastName", "Email", "PhoneNumber"],
  };

  const panelHasErrors = (
    panel: keyof typeof PANEL_FIELD_MAP,
    errors: FieldErrors,
  ): boolean => {
    return PANEL_FIELD_MAP[panel].some((field) => !!errors[field]);
  };

  /**
   * Handles saving or updating a user by calling the appropriate API function.
   *
   * @param {Function} apiFunction - The API function to call (_addUser or _updateUser).
   */
  const handleSaveOrUpdate = async (apiFunction: Function) => {
    if ((client.targetYear ?? 0) < CURRENT_YEAR) {
      props.handleCloseSnackBar({
        show: true,
        severity: "error",
        message: `Target year must be ${CURRENT_YEAR} or later`,
      });
      return;
    }

    if (typeof apiFunction === "function") {
      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("ClientId", String(client.clientId ?? 0));
        formData.append("ClientName", client.clientName ?? "");
        formData.append("ClientType", client.clientType ?? "");
        formData.append("Description", client.description ?? "");
        formData.append("Industry", client.industry ?? "");
        formData.append("Region", client.region ?? "");
        formData.append("Contact", String(client.contact ?? 0));
        formData.append("TargetYear", String(client.targetYear ?? 0));
        formData.append("FirstName", client.firstName ?? "");
        formData.append("LastName", client.lastName ?? "");
        formData.append("Email", client.email ?? "");
        formData.append("PhoneNumber", String(client.phoneNumber ?? ""));

        if (client.imageFile) {
          formData.append("ImageName", client.imageFile ?? "");
        }

        let res;
        if (client.clientId > 0) {
          res = await _updateClientInformation(formData);
        } else {
          res = await _addClient(formData);
        }

        if (res) {
          const message =
            client.clientId === 0
              ? `${client.clientName} Created Successfully !`
              : `${client.clientName} Updated Successfully !`;
          setLoading(false);
          props.handleCloseSnackBar({
            show: true,
            severity: "success",
            message: message,
          });
          if (client.clientId > 0) {
            props.onClientUpdated?.(client);
          } else {
            props.onClientCreated?.(client);
          }
          props.hideDialogHandler();
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [] }, // Accept only images
    maxSize: 10 * 1024 * 1024, // 10MB limit
    multiple: false, // Allow only one file at a time
  });

  const onValidSubmit = () => {
    handleSaveOrUpdate(
      client.clientId > 0 ? _updateClientInformation : _addClient,
    );
  };

  const onInvalidSubmit = async (formErrors: FieldErrors) => {
    await trigger();
    expandAccordionOnError(formErrors);

    const firstErrorField = Object.keys(formErrors)[0];

    setTimeout(() => {
      const el = document.querySelector(
        `[name="${firstErrorField}"]`,
      ) as HTMLElement | null;

      el?.focus();
    }, 150);
  };

  const expandAccordionOnError = (formErrors: FieldErrors) => {
    if (
      formErrors.ClientName ||
      formErrors.TargetYear ||
      formErrors.Region ||
      formErrors.Industry
    ) {
      setActiveTab(0);
      return;
    } else if (formErrors.PhoneNumber || formErrors.Email) {
      setActiveTab(1);
    }
  };

  // JSX structure for the AddUser component
  return (
    <Box sx={{ mx: 2, my: 1 }}>
      <form
        onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}
        noValidate
        data-testid="form"
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.95rem",
                minWidth: "auto",
                px: 3,
              },
              "& .Mui-selected": {
                color: "primary.main",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "primary.main",
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                  General Information
                  {panelHasErrors("panel1", errors) && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#E4610F",
                      }}
                    />
                  )}
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <ContactMailOutlinedIcon sx={{ fontSize: 15 }} />
                  Contact Information
                  {panelHasErrors("panel2", errors) && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#E4610F",
                      }}
                    />
                  )}
                </Box>
              }
            />
            <Tab label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <TrackChangesOutlinedIcon sx={{ fontSize: 15 }} />
                Set Target
              </Box>
            } />
            <Tab label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <ImageOutlinedIcon sx={{ fontSize: 15 }} />
                Client Image
              </Box>
            } />
          </Tabs>
        </Box>

        {/* Tab Panel 1 - General Information */}
        <Box
          sx={{ py: 2, display: activeTab === 0 ? "block" : "none" }}
          role="tabpanel"
          hidden={activeTab !== 0}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <TextField
                inputProps={{ "data-testid": "ClientName" }}
                {...register("ClientName", {
                  required: "ClientName is required",
                  validate: (value) => {
                    const clientName = value;

                    if (clientName.length === 0) {
                      return "Enter a client name";
                    }

                    return true;
                  },
                })}
                name="ClientName"
                value={client.clientName ?? ""}
                size="small"
                label="Client Name *"
                fullWidth
                onChange={handleInputChange}
                error={!!errors.ClientName}
                helperText={errors.ClientName?.message as string}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "0.813rem",
                    height: "36px",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.813rem" },
                  "& .MuiFormHelperText-root": { fontSize: "0.7rem", mt: 0.25 },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <FormControl fullWidth error={!!errors.ClientType}>
                <Select
                  data-testid="ClientType"
                  {...register("ClientType", {
                    required: "Client Type is required",
                    validate: (value) => {
                      if (!value || value === "") {
                        return "Please select a Client Type";
                      }
                      return true;
                    },
                  })}
                  name="ClientType"
                  value={
                    clientTypes.find((t) => t.label === client.clientType)
                      ?.value ?? ""
                  }
                  onChange={handleSelectInputChange}
                  displayEmpty
                  size="small"
                  fullWidth
                  sx={{
                    fontSize: "0.813rem",
                    height: "36px",
                    "& .MuiSelect-select": { py: 0.75 },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: "0.813rem" }}>
                    Select Type *
                  </MenuItem>
                  {clientTypes.map((t) => (
                    <MenuItem
                      key={t.id}
                      value={t.value}
                      sx={{ fontSize: "0.813rem" }}
                    >
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.ClientType && (
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      mt: 0.25,
                      ml: 1.75,
                      color: "error.main",
                    }}
                  >
                    {errors.ClientType?.message as string}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <TextField
                inputProps={{ "data-testid": "Description" }}
                {...register("Description", {
                  required: false,
                })}
                name="Description"
                value={client.description ?? ""}
                size="small"
                label="Description"
                fullWidth
                onChange={handleInputChange}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "0.813rem",
                    height: "36px",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.813rem" },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <FormControl fullWidth size="small" error={!!errors.Region}>
                <Select
                  data-testid="Region"
                  {...register("Region", {
                    required: "Region is required",
                    validate: (value) => {
                      if (!value || value === "") {
                        return "Please select a Region";
                      }
                      return true;
                    },
                  })}
                  name="Region"
                  value={
                    regionList.find((r) => r.regionName === client.region)
                      ?.value ?? ""
                  }
                  onChange={handleSelectInputChange}
                  displayEmpty
                  size="small"
                  fullWidth
                  sx={{
                    fontSize: "0.813rem",
                    height: "36px",
                    "& .MuiSelect-select": { py: 0.75 },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: "0.813rem" }}>
                    Select Region *
                  </MenuItem>
                  {regionList.map((t) => (
                    <MenuItem
                      key={t.regionId}
                      value={t.value}
                      sx={{ fontSize: "0.813rem" }}
                    >
                      {t.regionName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.Region && (
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      mt: 0.25,
                      ml: 1.75,
                      color: "error.main",
                    }}
                  >
                    {errors.Region?.message as string}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <FormControl fullWidth size="small" error={!!errors.Industry}>
                <Select
                  data-testid="Industry"
                  {...register("Industry", {
                    required: "Industry is required",
                    validate: (value) => {
                      if (!value || value === "") {
                        return "Please select an Industry";
                      }
                      return true;
                    },
                  })}
                  name="Industry"
                  value={
                    industryList.find((i) => i.label === client.industry)
                      ?.value ?? ""
                  }
                  onChange={handleSelectInputChange}
                  displayEmpty
                  size="small"
                  fullWidth
                  sx={{
                    fontSize: "0.813rem",
                    height: "36px",
                    "& .MuiSelect-select": { py: 0.75 },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: "0.813rem" }}>
                    Select Industry *
                  </MenuItem>
                  {industryList.map((t) => (
                    <MenuItem
                      key={t.id}
                      value={t.value}
                      sx={{ fontSize: "0.813rem" }}
                    >
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.Industry && (
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      mt: 0.25,
                      ml: 1.75,
                      color: "error.main",
                    }}
                  >
                    {errors.Industry?.message as string}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <TextField
                inputProps={{
                  "data-testid": "TargetYear",
                  inputMode: "numeric",
                  maxLength: 4,
                }}
                {...register("TargetYear", {
                  required: "Target year is required",
                  validate: (value) => {
                    const yearStr = value;

                    if (!/^\d{4}$/.test(yearStr)) {
                      return "Enter a valid 4-digit year";
                    }

                    const yearNum = Number(yearStr);

                    if (yearNum < CURRENT_YEAR) {
                      return `Target year must be ${CURRENT_YEAR} or later`;
                    }

                    return true;
                  },
                })}
                name="TargetYear"
                value={client.targetYear ?? ""}
                size="small"
                label="Target Year *"
                fullWidth
                onChange={handleInputChange}
                error={!!errors.TargetYear}
                helperText={errors.TargetYear?.message as string}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "0.813rem",
                    height: "36px",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.813rem" },
                  "& .MuiFormHelperText-root": { fontSize: "0.7rem", mt: 0.25 },
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Tab Panel 2 - Contact Information */}
        <Box
          sx={{ py: 2, display: activeTab === 1 ? "block" : "none" }}
          role="tabpanel"
          hidden={activeTab !== 1}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <TextField
                inputProps={{ "data-testid": "FirstName" }}
                {...register("FirstName", {
                  required: false,
                })}
                name="FirstName"
                value={client.firstName ?? ""}
                size="small"
                label="First Name"
                fullWidth
                onChange={handleInputChange}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "0.813rem",
                    height: "36px",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.813rem" },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <TextField
                inputProps={{ "data-testid": "LastName" }}
                {...register("LastName", {
                  required: false,
                })}
                name="LastName"
                value={client.lastName ?? ""}
                size="small"
                label="Last Name"
                fullWidth
                onChange={handleInputChange}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "0.813rem",
                    height: "36px",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.813rem" },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <TextField
                inputProps={{ "data-testid": "Email" }}
                {...register("Email", {
                  required: "Email is required",
                  pattern: {
                    value:
                      /^[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,253}\.[a-zA-Z]{2,63}$/,
                    message: "Enter a valid email address",
                  },
                })}
                name="Email"
                value={client.email ?? ""}
                size="small"
                label={
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    Email *
                    <Tooltip title="This contact will receive onboarding instructions and notifications.">
                      <InfoOutlinedIcon
                        fontSize="small"
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                fullWidth
                onChange={handleInputChange}
                error={!!errors.Email} // boolean
                helperText={
                  errors.Email ? (errors.Email.message as string) : ""
                } // string or ReactNode
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "0.813rem",
                    height: "36px",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.813rem" },
                  "& .MuiFormHelperText-root": { fontSize: "0.7rem", mt: 0.25 },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <TextField
                inputProps={{
                  "data-testid": "PhoneNumber",
                  maxLength: 15,
                  inputMode: "tel", // 📱 mobile-friendly keypad
                }}
                {...register("PhoneNumber", {
                  required: "Phone number is required",
                  validate: (value) =>
                    /^\+?\d{1,14}$/.test(value) ||
                    "Phone number must contain only digits and may start with +",
                })}
                name="PhoneNumber"
                value={client.phoneNumber ?? ""}
                size="small"
                label={
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    Phone Number *
                    <Tooltip title="Include country code (e.g. +91)">
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                fullWidth
                onChange={handleInputChange}
                error={!!errors.PhoneNumber}
                helperText={errors.PhoneNumber?.message as string}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "0.813rem",
                    height: "36px",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.813rem" },
                  "& .MuiFormHelperText-root": { fontSize: "0.7rem", mt: 0.25 },
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Tab Panel 3 - Set Target */}
        <Box
          sx={{ py: 2, display: activeTab === 2 ? "block" : "none" }}
          role="tabpanel"
          hidden={activeTab !== 2}
        >
          {isEditMode ? (
            <ClientSetTarget
              clientId={client.clientId}
              handleCloseSnackBar={props.handleCloseSnackBar}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 120,
                color: "text.secondary",
                gap: 1,
              }}
            >
              <Typography variant="body2" textAlign="center">
                This section will be available once the client has been successfully created.
              </Typography>
              <Typography sx={{ fontSize: "0.62rem", color: "text.secondary", lineHeight: 1.3, fontStyle: "italic" }}>
                <strong>Note:</strong> Targets are used for GHG reduction tracking across all locations.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tab Panel 4 - Client Image */}
        <Box
          sx={{ py: 2, display: activeTab === clientImageTabIndex ? "block" : "none" }}
          role="tabpanel"
          hidden={activeTab !== clientImageTabIndex}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Image Preview */}
            <Grid item xs={12} sm={12}>
              <Typography variant="body1" textAlign="center" sx={{ mb: 0 }}>
                Upload an image your client.
              </Typography>
              <Box sx={{ maxWidth: 600, margin: "auto", mt: 0 }}>
                <Card elevation={3}>
                  <CardContent>
                    {base64Image && (
                      <>
                        <Box
                          component="img"
                          src={base64Image}
                          alt="Uploaded preview"
                          sx={{
                            display: "block",
                            margin: "auto",
                            maxWidth: "100%",
                            maxHeight: 180,
                            borderRadius: 1,
                          }}
                        />
                        <Divider sx={{ my: 2 }} />
                      </>
                    )}
                    <Box
                      {...getRootProps()}
                      sx={{
                        border: "2px dashed #ccc",
                        borderRadius: 2,
                        padding: 3,
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: "#f9f9f9",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                      }}
                      data-testid="dropzone"
                    >
                      <input {...getInputProps()} />
                      <CloudUploadIcon sx={{ fontSize: 40, color: "orange" }} />
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {selectedFile?.name ?? "Drag and drop a file here"}
                      </Typography>
                      <Button
                        variant="outlined"
                        color="warning"
                        sx={{ mt: 2, textTransform: "none" }}
                      >
                        Browse Image
                      </Button>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1, color: "gray" }}
                      >
                        Maximum file size is 10 MB
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            display: activeTab === 2 ? "none" : "flex",
            justifyContent: "flex-end",
            zIndex: 10,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            data-testid="btnSave"
            size="small"
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              py: 0.25,
              px: 1,
              minHeight: "auto",
              mb: 1,
              mr: 1,
            }}
          >
            {client.clientId > 0 ? "Update Client" : "Save Client"}
          </Button>
        </Box>
      </form>
      {loading && <Spinner size={80} data-testid="spinner" />}
    </Box>
  );
};

export default AddClient;
